import { useState, useMemo } from 'react'
import { getCatalog, type CatalogCategory, type CatalogBlock } from '@/features/shared/services/block-loader'
import { useStudioStore } from '@/store/studio-store'
import { ChevronDown, Search } from 'lucide-react'

export function BlockCatalogPanel() {
  const [search, setSearch] = useState('')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const catalog = useMemo(() => getCatalog(), [])

  const filtered = useMemo(() => {
    if (!search.trim()) return catalog.categories
    const q = search.toLowerCase()
    return catalog.categories
      .map((cat) => ({
        ...cat,
        blocks: cat.blocks.filter(
          (b) =>
            b.id.toLowerCase().includes(q) ||
            b.description.toLowerCase().includes(q) ||
            b.tags.some((t) => t.toLowerCase().includes(q)),
        ),
      }))
      .filter((cat) => cat.blocks.length > 0)
  }, [catalog.categories, search])

  const toggleCategory = (id: string) => {
    setExpanded((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  return (
    <div className="space-y-4">
      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <input
          type="text"
          placeholder="Search blocks..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded-xl bg-background focus:outline-none focus:ring-1 focus:ring-ring"
        />
      </div>

      <p className="text-xs text-muted-foreground">
        {catalog.totalBlocks} blocks across {catalog.categories.length} categories
      </p>

      {/* Categories accordion */}
      <div className="space-y-1">
        {filtered.map((cat) => (
          <CategoryAccordion
            key={cat.id}
            category={cat}
            isOpen={expanded.has(cat.id) || search.trim().length > 0}
            onToggle={() => toggleCategory(cat.id)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-8">
            No blocks match "{search}"
          </p>
        )}
      </div>
    </div>
  )
}

function CategoryAccordion({
  category,
  isOpen,
  onToggle,
}: {
  category: CatalogCategory
  isOpen: boolean
  onToggle: () => void
}) {
  const [expandedBlock, setExpandedBlock] = useState<string | null>(null)

  return (
    <div className="border border-border rounded-xl overflow-hidden">
      <button
        className="w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent/50 transition-colors"
        onClick={onToggle}
      >
        <span className="font-medium">{category.label}</span>
        <span className="flex items-center gap-2 text-xs text-muted-foreground">
          {category.blocks.length}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>
      {isOpen && (
        <div className="border-t border-border px-3 py-2 space-y-2">
          {category.blocks.map((block) => (
            <BlockRow
              key={block.id}
              block={block}
              isExpanded={expandedBlock === block.id}
              onToggle={() => setExpandedBlock(expandedBlock === block.id ? null : block.id)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function BlockRow({
  block,
  isExpanded,
  onToggle,
}: {
  block: CatalogBlock
  isExpanded: boolean
  onToggle: () => void
}) {
  return (
    <div>
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-2 py-1.5 hover:bg-accent/50 rounded px-1 -mx-1 transition-colors"
      >
        <div className="flex-1 min-w-0 text-left">
          <p className="text-xs font-medium truncate">{block.id}</p>
          <p className="text-[11px] text-muted-foreground line-clamp-1">{block.description}</p>
        </div>
        <span className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${
          block.complexity === 'simple' ? 'bg-primary/10 text-primary' :
          block.complexity === 'moderate' ? 'bg-yellow-500/10 text-yellow-600' :
          'bg-red-500/10 text-red-600'
        }`}>
          {block.complexity}
        </span>
      </button>
      {isExpanded && <BlockDetail block={block} />}
    </div>
  )
}

function BlockDetail({ block }: { block: CatalogBlock }) {
  const projectList = useStudioStore((s) => s.projectList)
  const openTemplateDetail = useStudioStore((s) => s.openTemplateDetail)
  const usingTemplates = projectList.filter((t) => t.blocks.includes(block.id))

  return (
    <div className="ml-4 mt-2 mb-3 p-3 rounded-xl bg-muted/50 border border-border/50 space-y-3 text-xs">
      {/* Renderer Types */}
      {block.rendererTypes.length > 0 && (
        <div>
          <span className="text-muted-foreground">Renderer Types:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {block.rendererTypes.map((t) => (
              <span key={t} className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">{t}</span>
            ))}
          </div>
        </div>
      )}

      {/* Composability */}
      <div>
        <span className="text-muted-foreground">Position:</span>{' '}
        <span>{block.composability.position}</span>
        {block.composability.standalone && (
          <span className="ml-2 text-primary">standalone</span>
        )}
      </div>

      {/* Pairs With */}
      {block.composability.pairs_with.length > 0 && (
        <div>
          <span className="text-muted-foreground">Pairs with:</span>{' '}
          <span>{block.composability.pairs_with.join(', ')}</span>
        </div>
      )}

      {/* Tags */}
      {block.tags.length > 0 && (
        <div>
          <span className="text-muted-foreground">Tags:</span>
          <div className="flex flex-wrap gap-1 mt-1">
            {block.tags.map((tag) => (
              <span key={tag} className="px-1.5 py-0.5 bg-background border border-border rounded text-[10px]">{tag}</span>
            ))}
          </div>
        </div>
      )}

      {/* Used in Templates */}
      {usingTemplates.length > 0 && (
        <div>
          <span className="text-muted-foreground">Used in:</span>
          <div className="mt-1 space-y-1">
            {usingTemplates.map((t) => (
              <button
                key={t.id}
                onClick={() => openTemplateDetail(t.id)}
                className="text-foreground hover:underline block"
              >
                {t.name} &rarr;
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
